// A kiadás-sor VONATKOZÁSI HÓNAP cellája — helyben szerkeszthető legördülő, a kategória-cella mintájára.
//
// MIT DÖNT EL: melyik hónap EREDMÉNYÉT terheli a tétel. Eredményszemlélet: a költség ahhoz a hónaphoz
// tartozzon, amikor a munka történt, nem ahhoz, amikor a bizonylat kelt. A bevétel-oldalon ez levezethető (a
// számla-tételen ott a szolgáltatási periódus), itt NEM: egy szállítói számla dátumából nem derül ki, melyik
// hónap költsége — a NAV-import szeptemberben hozhat egy augusztusi teljesítésű bérleti díjat, egy januári
// szoftver-licenc pedig az egész évre szól.
//
// A NAV-sor is átsorolható: ugyanaz az elv, mint a besorolásnál — a bizonylat mezőihez nem nyúlunk, de azt,
// hogy melyik hónap eredményébe tartozik, nekünk kell eldöntenünk.
//
// ⚠️ A CELLA NEM MENT. A választás csak a sor FÜGGŐ állapotát állítja; a mentést a sor végén álló „Mentés"
// gomb indítja (user-döntés) — a kategóriával EGYÜTT, egyetlen kérésben. A kattintásra induló írás egy
// elvétett választást azonnal kiküldött, és a visszavonás egy MÁSODIK írás volt (két verzió-lépés a
// bizonylaton, két audit-bejegyzés).
//
// A MENTÉS UTÁN a sor kiesik a nézett hónapból, ha másik hónapra került — a lista a VONATKOZÁSI hónapon áll,
// tehát egy helyben frissített sor olyan tételt mutatna, ami már nem ehhez a hónaphoz tartozik, és a lap
// összegében sem szerepel. Ezt a listát birtokló hook végzi (egy helyen a kategóriával).

import type { ExpenseRow } from '@atlas/contracts';
import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Text } from '@/components/ui/text';
import { formatMonthLabel } from '@/lib/month';

/** A „bizonylat szerint" választás értéke (a `null`-t a Select nem tudja hordozni). */
const DERIVED = '__derived__';

/**
 * A választható hónapok: a nézett hónap KÖRÜL egy ablak, IDŐBEN VISSZAFELÉ rendezve (a legkésőbbi elöl).
 *
 * Miért ablak és nem szabad dátum-mező: a valódi átsorolás néhány hónapnyi (a NAV késve hoz, az éves licenc
 * az év hónapjaira oszlik), egy szabad mező viszont elgépelhető — és egy elgépelt 2062-es hónap némán
 * eltüntetné a tételt minden kimutatásból.
 *
 * Miért CSÖKKENŐ a sorrend: a listában is a legfrissebb bizonylat áll elöl, és az átsorolás túlnyomórészt
 * a közelmúltba mutat — a naptár így ugyanabba az irányba olvasódik, mint a lista.
 *
 * MIÉRT 6 VISSZA ÉS 3 ELŐRE (mérés, nem érzés — 205 élő NAV-soron): a TELJESÍTÉS és a KIÁLLÍTÁS hónapja
 * a sorok 82%-ában AZONOS, és a legnagyobb mért eltérés HÁROM hónap (1 hó: 20 sor · 2 hó: 7 · 3 hó: 4);
 * előre pedig 5 sor mutatott (előre fizetett díj). A NAV sem hoz régi bizonylatot: 205-ből 204 ugyanabban
 * a hónapban érkezett, ahogy kiállították. A 6 tehát a mért maximum DUPLÁJA, és fedi az év végi zárást is
 * (februárban a decemberi költség 2 hónap vissza).
 *
 * MIÉRT NEM TÖBB — ez ACCRUAL-érv, nem kényelmi: a veszélyes hiba az ÉV-HATÁR átlépése (a tavalyi költség
 * az idei eredménybe csúszik), és azt egy hosszú, gyorsan görgetett listában KÖNNYEBB elvéteni. Az éves
 * licenc sem érv a hosszú lista mellett: a modul EGY hónapot rendel egy tételhez, szétosztani nem tud —
 * azon a 12 hónapos elérés sem segítene (a szétterítés külön feladat, lásd a bevétel-oldali `spreadActuals`-t).
 *
 * A SZELEP: ha a soron olyan hónap áll, ami kiesik az ablakból, azt a hívó BESZÚRJA a listába — egy
 * korábban beállított, távoli hónap tehát nem vész el, és nem is íródik felül.
 */
export function monthOptions(current: string, back = 6, forward = 3): string[] {
  const m = /^(\d{4})-(\d{2})$/.exec(current);
  if (m === null) return [];
  const year = Number(m[1]);
  const month = Number(m[2]);
  const out: string[] = [];
  for (let i = forward; i >= -back; i--) {
    const d = new Date(Date.UTC(year, month - 1 + i, 1));
    out.push(`${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

export interface ExpenseMonthCellProps {
  row: ExpenseRow;
  /** A MEGJELENÍTENDŐ érték: a függő választás, ha van, különben a sor kézi értéke. `null` = bizonylat szerint. */
  value: string | null;
  /** A választás — a hívó a sor függő állapotába írja (nem ment). */
  onChange: (accountingMonth: string | null) => void;
  /** A NÉZETT hónap: a választható ablak horgonya (nem a sor saját hónapja). */
  viewMonth: string;
  canManage: boolean;
}

export function ExpenseMonthCell({
  row,
  value,
  onChange,
  viewMonth,
  canManage,
}: ExpenseMonthCellProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  /** Az EFFEKTÍV hónap: a kézi érték, annak híján a bizonylaté — ugyanaz a szabály, amit a szerver alkalmaz. */
  const effective = value ?? row.documentMonth;

  // A hónap a NYELV szerinti alakban jelenik meg („2026. szeptember" / „September 2026") — ugyanazzal a
  // formázóval, mint a hónap-léptető és a lista dátumai. A nyers `YYYY-MM` gépi alak, nem olvasói.
  const label =
    effective === '' ? t('expenses.month.unknown') : formatMonthLabel(effective, locale);

  if (!canManage) {
    return (
      <div className="min-w-0">
        <span className="truncate">{label}</span>
        {value === null && (
          <Text variant="secondary" tone="tertiary" as="div" className="truncate text-xs">
            {t('expenses.month.derived')}
          </Text>
        )}
      </div>
    );
  }

  // A BIZONYLAT SZERINTI hónap a SZERVERTŐL jön (`documentMonth`), nem a kliens vezeti le a dátumokból.
  // Ugyanabból a szabályból áll a lista hónap-szűrése is — két helyen vezetve a jelölés és a lista
  // elcsúszhatna egymástól.
  const derivedMonth = row.documentMonth;

  // EGY HÓNAP = EGY SOR a listában (user-döntés). A bizonylat szerinti hónap nem KÜLÖN tétel, hanem a saját
  // helyén, megjelölve — így nem áll ott ugyanaz a hónap kétszer, és átsorolt soron is LÁTSZIK, mit mondana
  // a bizonylat. Ennek a hónapnak a választása = „vissza a bizonylathoz" (a szerverre `null` megy).
  const options = monthOptions(viewMonth);
  // Az ÉPPEN beállított és a BIZONYLAT SZERINTI hónap akkor is legyen a listában, ha kiesik az ablakból:
  // az első nélkül a legördülő a saját értékét nem tudná megmutatni, a második nélkül a jelölés tűnne el.
  for (const extra of [effective, derivedMonth]) {
    if (extra !== '' && !options.includes(extra)) options.push(extra);
  }
  options.sort().reverse();

  /** A jelölt hónap a `null`-t (bizonylat szerinti) hordozza, a többi önmagát. */
  const valueFor = (m: string): string => (m === derivedMonth ? DERIVED : m);
  // Ha a kézi érték UGYANAZ, mint a bizonylaté, a jelölt tételt mutatjuk kiválasztottként — különben a
  // trigger üres maradna (nincs a listában olyan tétel, ami a nyers hónapot hordozná).
  const selected = value !== null && value !== derivedMonth ? value : DERIVED;

  return (
    <div className="min-w-0">
      <Select value={selected} onValueChange={(next) => onChange(next === DERIVED ? null : next)}>
        <SelectTrigger
          className="h-8 w-full"
          aria-label={t('expenses.month.aria', { supplier: row.supplierName })}
        >
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>
          {options.map((m) => (
            <SelectItem key={m} value={valueFor(m)}>
              {m === derivedMonth
                ? `${formatMonthLabel(m, locale)} (${t('expenses.month.derived')})`
                : formatMonthLabel(m, locale)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
